import { apiClient } from "../client";

export interface Country {
  id: string;
  name: string;
  flag: string;
  iso2: string;
}

export interface State {
  id: string;
  name: string;
  countryCode: string;
  iso2: string;
}

export interface City {
  id: string;
  name: string;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export class LocationService {
  static async getCountries(): Promise<Country[]> {
    try {
      return await apiClient.get<Country[]>("/location/countries");
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch states by country code
   * Public endpoint - no authentication required
   */
  static async getStates(countryCode: string): Promise<State[]> {
    try {
      return await apiClient.get<State[]>("/location/states", {
        params: { countryCode },
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch cities by country and state code
   * Public endpoint - no authentication required
   */
  static async getCities(
    countryCode: string,
    stateCode: string,
  ): Promise<City[]> {
    try {
      return await apiClient.get<City[]>("/location/cities", {
        params: { countryCode, stateCode },
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   */
  private static handleError(error: unknown): Error {
    const axiosError = error as AxiosErrorResponse;

    if (axiosError.response?.data) {
      const message = Array.isArray(axiosError.response.data.message)
        ? axiosError.response.data.message.join(", ")
        : axiosError.response.data.message;
      return new Error(message || "An unexpected error occurred");
    }

    if (axiosError.message) {
      return new Error(axiosError.message);
    }

    return new Error("An unexpected error occurred");
  }
}
